import type { CognitiveProfile } from '../types/cognitiveProfile'
import type { ProactiveSuggestionPayload } from '../types/ai'

export type AiProactiveTrigger = ProactiveSuggestionPayload & {
  quickButtons: Array<{ label: string; value: string }>
  matchedHistorySummary?: string
}

const tokens = (value: string) => new Set(
  value.toLowerCase().match(/[a-z0-9]{3,}/g) ?? [],
)

export const tokenSimilarity = (left: string, right: string): number => {
  const leftTokens = tokens(left)
  const rightTokens = tokens(right)
  if (leftTokens.size === 0 || rightTokens.size === 0) return 0
  let overlap = 0
  leftTokens.forEach((token) => {
    if (rightTokens.has(token)) overlap += 1
  })
  return overlap / Math.min(leftTokens.size, rightTokens.size)
}

export const findProactiveTriggers = ({
  query,
  profile,
  createdTask,
}: {
  query: string
  profile: CognitiveProfile
  createdTask?: { title: string; startDate?: unknown; endDate?: unknown }
}): AiProactiveTrigger[] => {
  const triggers: AiProactiveTrigger[] = []
  if (createdTask && !createdTask.startDate && !createdTask.endDate) {
    triggers.push({
      id: 'GAP_ANALYSIS',
      kind: 'GAP_ANALYSIS',
      message: 'Task created. Do you want me to suggest a timeline placement based on your typical project velocity?',
      acceptancePrompt: 'Suggest a date for the undated task based on my workflow and return a preview.',
      quickButtons: [
        { label: 'Suggest placement', value: 'Suggest a date based on my workflow.' },
        { label: 'Leave undated', value: 'Leave this task undated.' },
      ],
    })
  }

  const archetypes = Array.isArray(profile.preferences.intentArchetypes)
    ? profile.preferences.intentArchetypes.filter((value): value is string => typeof value === 'string')
    : []
  const candidates = [
    ...profile.projectHistory.map((outcome) => outcome.summary),
    ...archetypes,
  ]
  const best = candidates
    .map((summary) => ({ summary, score: tokenSimilarity(query, summary) }))
    .sort((left, right) => right.score - left.score)[0]
  if (best && best.score >= 0.6) {
    triggers.push({
      id: 'CONTEXT_LINKER',
      kind: 'CONTEXT_LINKER',
      message: `You've handled similar work before: "${best.summary}". Do you want me to use that context?`,
      acceptancePrompt: `Use relevant lessons from this past context and return a preview: ${best.summary}`,
      matchedHistorySummary: best.summary,
      quickButtons: [
        { label: 'Use past context', value: `Use relevant lessons from: ${best.summary}` },
        { label: 'Ignore it', value: 'Do not use past-project context.' },
      ],
    })
  }
  return triggers
}
